import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import MockPlayer from "@/pages/detail/video/components/MockPlayer";
import { categoryMap, formatDate, formatViews, getVideo, relatedVideos } from "@/lib/content";
import { useSeo } from "@/hooks/useSeo";

export default function VideoDetailPage() {
  const { videoId } = useParams();
  const video = getVideo(videoId);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: video ? `${video.title} - 抖阳视频内容` : "视频不可用 - 抖阳",
    description: video ? video.summary : "该视频不存在或已下架，返回抖阳浏览更多原创视频内容。",
    keywords: video ? `抖阳视频,${categoryMap[video.category]?.name},${video.tags.join(",")}` : "抖阳视频",
  });

  useEffect(() => {
    setLoading(true);
    window.scrollTo({ top: 0 });
    const timer = window.setTimeout(() => setLoading(false), 320);
    return () => window.clearTimeout(timer);
  }, [videoId]);

  if (!video) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="mx-auto w-full max-w-[900px] flex-1 px-4 py-20 md:px-6">
          <EmptyState
            icon="ri-video-off-line"
            title="视频不可用"
            description="这个视频可能已被删除或链接有误。你可以回到首页看看其他原创视频。"
            action={
              <Link
                to="/"
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm text-background-50 hover:bg-primary-600"
              >
                返回首页
                <i className="ri-arrow-right-line" />
              </Link>
            }
          />
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = categoryMap[video.category];
  const related = relatedVideos(video, 4);
  const bottomRelated = relatedVideos(video, 3);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100/60 px-4 py-3 md:px-6">
          <div className="mx-auto flex max-w-[1400px] flex-wrap items-center justify-between gap-2">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: category?.name ?? "分类", to: `/category/${video.category}` },
                { label: "视频详情" },
              ]}
            />
            <Link
              to={`/category/${video.category}`}
              className="flex items-center gap-1 text-xs text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-arrow-left-line" />
              返回{category?.name}频道
            </Link>
          </div>
        </div>

        <div className="mx-auto max-w-[1400px] px-4 py-6 md:px-6 md:py-8">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <div className="skeleton-shimmer aspect-video w-full rounded-lg" />
                <div className="skeleton-shimmer mt-5 h-8 w-3/4 rounded" />
                <div className="skeleton-shimmer mt-3 h-4 w-1/2 rounded" />
              </div>
              <div className="lg:col-span-4">
                <div className="skeleton-shimmer h-64 w-full rounded-lg" />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
              <div className="min-w-0 lg:col-span-8">
                <div className="mb-4 flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category/${video.category}`}
                    className="rounded bg-primary-500 px-2.5 py-1 text-[11px] font-medium text-background-50 hover:bg-primary-600"
                  >
                    {category?.name}
                  </Link>
                  <span className="flex items-center gap-1 rounded border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600">
                    <i className="ri-time-line" />
                    {video.duration}
                  </span>
                  <span className="flex items-center gap-1 rounded border border-background-300 px-2.5 py-1 text-[11px] text-foreground-600">
                    <i className="ri-play-circle-line" />
                    {formatViews(video.views)} 次播放
                  </span>
                </div>

                <h1 className="mb-4 font-heading text-xl font-bold leading-snug tracking-tight text-foreground-950 md:text-3xl">
                  {video.title}
                </h1>

                <MockPlayer cover={video.cover} title={video.title} duration={video.duration} />

                <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-b border-background-200 pb-4">
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                      {video.author.name.slice(0, 1)}
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{video.author.name}</p>
                      <p className="mt-0.5 text-xs text-foreground-400">
                        {video.author.role} · {formatDate(video.date)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-xs text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-heart-3-line" />
                      喜欢
                    </button>
                    <Link
                      to={`/category/${video.category}`}
                      className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                    >
                      <i className="ri-list-unordered" />
                      同类视频
                    </Link>
                  </div>
                </div>

                <div className="mt-5 rounded-lg bg-background-100/70 p-4 md:p-5">
                  <h2 className="text-sm font-semibold text-foreground-950">视频简介</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{video.summary}</p>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                    本期由抖阳视频组独立拍摄与剪辑，采访与拍摄历时两周。视频中的观点仅代表受访者个人，不代表平台立场。
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {video.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/search?q=${encodeURIComponent(tag)}`}
                        className="flex items-center gap-1 rounded-full bg-background-200/80 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                      >
                        <i className="ri-price-tag-3-line" />
                        {tag}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-4 flex items-start gap-2 border-t border-background-200 pt-3 text-[11px] text-foreground-400">
                    <i className="ri-information-line mt-0.5" />
                    说明：视频播放区域为前端 UI 演示，不加载真实视频流，也没有下载入口。
                  </div>
                </div>
              </div>

              <aside className="min-w-0 lg:col-span-4">
                <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <div className="mb-2 flex items-end justify-between border-b border-background-200 pb-3">
                    <div>
                      <p className="font-label text-[10px] uppercase tracking-[0.22em] text-primary-600">Up next</p>
                      <h2 className="mt-1 font-heading text-sm font-bold text-foreground-950">相关推荐</h2>
                    </div>
                    <span className="text-[11px] text-foreground-400">{related.length} 个</span>
                  </div>
                  <div className="space-y-1">
                    {related.map((item) => (
                      <VideoCard key={item.id} video={item} variant="horizontal" />
                    ))}
                  </div>
                </div>

                <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50/70 p-4">
                  <p className="font-label text-[10px] uppercase tracking-[0.2em] text-primary-700">Channel</p>
                  <h2 className="mt-1.5 font-heading text-sm font-bold text-foreground-950">
                    {category?.name}频道
                  </h2>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">{category?.slogan}</p>
                  <Link
                    to={`/category/${video.category}`}
                    className="mt-3 flex cursor-pointer items-center justify-center gap-1.5 rounded-md bg-primary-500 py-2 text-xs text-background-50 hover:bg-primary-600"
                  >
                    进入频道
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </aside>
            </div>
          )}
        </div>

        <section className="border-t border-background-200 bg-background-100/60 px-4 py-10 md:px-6 md:py-12">
          <div className="mx-auto max-w-[1400px]">
            <div className="mb-5 flex items-end justify-between gap-3">
              <h2 className="font-heading text-lg font-bold text-foreground-950">看过这个视频的人也在看</h2>
              <span className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-400">
                More videos
              </span>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {bottomRelated.map((item) => (
                <VideoCard key={item.id} video={item} />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}